import Dialog, { IDialogProps } from '@/ui/dialog';
import { Close, CollectionActive, Copy, Search } from '@/ui/svg';
import { TokenWithChainIcon } from '@/ui/tokenImage';
import { formatEnglishNumber } from '@/utils';
import { useMarketData, useSearch } from '@bitbot/model';
import clsx from 'clsx';
import { debounce } from 'lodash';
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';
import LoadingMarketDataSkeleton from './common/skeleton/loading-market-data';
import DecimalFormat from './common/decimal-format';
import { sendGAEvent } from '@next/third-parties/google';
import { useRouter } from 'next/router';

export default function SearchDialog(props: IDialogProps) {
  const {
    input,
    setInput,
    search,
    searchedList,
    isSearching,
    isNoResult,
    onChangePair,
  } = useSearch();
  const router = useRouter();
  const { pairList, favList } = useMarketData();

  const [activeTab, setActiveTab] = useState('base');

  const triggerSearch = useCallback(
    debounce((input: string, search: (input: string) => void) => {
      if (input !== '') {
        search(input);
      }
    }, 500),
    [],
  );

  useEffect(() => {
    triggerSearch(input, search);
  }, [input]);

  useEffect(() => {
    if (props.open) {
      setInput('');
      setTimeout(() => {
        let input = document.getElementById('search') as HTMLInputElement;
        if (input) {
          input.focus();
        }
      }, 500);
    }
  }, [props.open]);

  const list =
    input.trim() === ''
      ? activeTab === 'fav'
        ? favList
        : pairList
      : searchedList;

  const showLoading = input.trim() !== '' && isSearching;
  const handleClickBack = () => {
    props.toggle(false);
  };
  return (
    <Dialog
      {...props}
      className="!bg-primary/90"
      hideUnderline={true}
      hscreen={true}
    >
      <div className="h-screen overflow-y-auto bottom-safe-area px-4 pt-3">
        <div className="flex">
          <div className="px-1 h-[50px] flex items-center justify-between sticky top-0 left-0 right-0 z-10 safe-area-padding-top">
            <a className="cursor-pointer" onClick={handleClickBack}>
              <svg
                className="fill-[primary] h-[36px] w-[36px]"
                viewBox="0 0 1024 1024"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                p-id="13465"
                width="200"
                height="200"
              >
                <path
                  d="M810.666667 477.866667H234.666667c4.266667-25.6 17.066667-46.933333 38.4-64l179.2-179.2-46.933334-42.666667-179.2 174.933333L81.066667 512l320 320 46.933333-46.933333-179.2-179.2c-17.066667-17.066667-29.866667-42.666667-38.4-64H810.666667v-64z"
                  p-id="13466"
                ></path>
              </svg>
            </a>
          </div>
          <div className="flex flex-grow bg-secondary py-3 px-4 rounded-[16px] items-center gap-3 text-secondary">
            <Search className="w-[22px] h-[22px]" />
            <input
              value={input}
              placeholder="Long press to paste"
              id="search"
              onChange={e => {
                setInput(e.target.value.trim());
              }}
              className="w-0 flex-grow outline-none text-primary text-lg font-semibold bg-secondary placeholder:text-tertiary"
            />
            {input.trim() !== '' && (
              <div
                className="cursor-pointer text-tertiary"
                onClick={() => {
                  setInput('');
                }}
              >
                <Close />
              </div>
            )}
          </div>
        </div>

        {input.trim() === '' && (
          <div className="flex gap-[0.375rem] mt-6">
            <div
              onClick={() => {
                if (activeTab !== 'fav') {
                  setActiveTab('fav');
                }
              }}
              className={clsx(
                'w-10 h-10 flex items-center justify-center rounded-[8px] border-[1px] cursor-pointer',
                activeTab === 'fav'
                  ? 'border-secondary'
                  : 'border-primary',
              )}
            >
              <CollectionActive className="text-[#FFC700]" />
            </div>
            <div
              onClick={() => {
                if (activeTab !== 'base') {
                  setActiveTab('base');
                }
              }}
              className={clsx(
                'w-10 h-10 flex items-center justify-center rounded-[8px] border-[1px] cursor-pointer',
                activeTab === 'base'
                  ? 'border-secondary'
                  : 'border-primary',
              )}
            >
              <Image
                width={19.2}
                height={19.2}
                alt="base"
                src="/chain/base.svg"
              />
            </div>
          </div>
        )}
        {input.trim() !== '' ? (
          !showLoading && isNoResult ? (
            ''
          ) : (
            <div className="text-secondary mt-6 text-base font-semibold">
              Search Result
            </div>
          )
        ) : (
          <div className="text-secondary mt-[18px] text-base font-semibold">
            {activeTab === 'fav' ? 'Favorite' : 'Trending'} tokens
          </div>
        )}

        {showLoading ? (
          <LoadingMarketDataSkeleton />
        ) : isNoResult && input.trim() !== '' ? (
          <div className="flex flex-col items-center w-[250px] mx-auto mt-16">
            <div className="text-center font-bold">No results found</div>
            <div className="text-center text-tertiary tracking-[-0.2px] mt-1">
              The address you typed either does not exist or is spelled
              incorrectly
            </div>
          </div>
        ) : (
          <>
            {list.length > 0 ? (
              <>
                <div className="flex flex-col text-tertiary text-xs leading-[16px]">
                  {list.map((pair, index) => {
                    if (pair) {
                      return (
                        <div
                          className="flex py-4 items-center cursor-pointer"
                          key={index}
                          onClick={() => {
                            onChangePair(pair.pairAddress);
                            sendGAEvent(
                              'event',
                              `trade_${pair.baseToken.symbol}_${pair.quoteToken.symbol}`,
                            );
                          }}
                        >
                          <div className="flex-grow flex items-center gap-2">
                            <TokenWithChainIcon
                              chainName={pair.chainId}
                              chainSrc="/chain/base.svg"
                              tokenName={pair.baseToken.symbol}
                              tokenSrc={pair.logo}
                              className="w-[2rem] h-[2rem]"
                              borderWidth={4}
                            />
                            <div className="flex flex-col">
                              <div className="flex items-center">
                                <div className="text-base font-bold leading-[1.5] text-primary max-w-[20vw] truncate">
                                  {pair.baseToken.symbol}
                                </div>
                                <span className="m-1">/</span>
                                {pair.quoteToken.symbol}
                              </div>
                              <div> {pair.baseToken.name}</div>
                            </div>
                          </div>
                          <div className="w-[100px] text-right flex flex-col">
                            <div className="text-primary font-bold leading-[1.5] text-base">
                              {formatEnglishNumber(+pair.fdv)}
                            </div>
                            <div className="leading-[16px] self-end">
                              <DecimalFormat
                                value={pair.priceUsd}
                                isUsd
                                format={4}
                              />
                            </div>
                          </div>
                          <div className="w-[25%] flex justify-end">
                            <div
                              className={clsx(
                                'h-7 flex items-center justify-center text-base font-bold text-right',
                                +pair.priceChange.h24 < 0
                                  ? 'text-red-accent'
                                  : 'text-green-accent',
                              )}
                            >
                              {+pair.priceChange.h24 >= 0 ? '+' : '-'}
                              {formatEnglishNumber(
                                Math.abs(+pair.priceChange.h24),
                              )}
                              %
                            </div>
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              </>
            ) : activeTab === 'fav' ? (
              <div className="pt-[120px] font-nunito_sans">
                <div className="text-lg text-center text-primary leading-[26px] font-bold">
                  Your favorites list is empty
                </div>
                <div className="text-tertiary text-center pt-2 text-base">
                  Browse and favorite tokens
                </div>
              </div>
            ) : (
              ''
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
